import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Target,
  TrendingUp,
  Users,
  BookOpen,
  Briefcase,
  Star,
  AlertCircle,
  CheckCircle2,
  Lightbulb,
  ChevronDown,
  FileText,
  Loader2,
  ExternalLink,
  Rocket,
  GraduationCap,
  Map,
  UserCheck,
  Globe,
  Youtube,
  BarChart3,
  Mail,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { authHeaders } from "@/lib/auth";
import { SectionErrorBoundary, EmptyState, levelColor } from "@/components/ReportSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_COLORS = {
  "Consulting": "#2C3E50",
  "Product Management": "#5D6D7E",
  "Finance / PE / VC": "#8B6F47",
  "Marketing": "#7D5A5A",
  "Sales": "#4A6FA5",
  "Data": "#5B7065",
  "HR": "#856088",
  "Founder's Office / Chief of Staff": "#C49A3C",
  "Operations & Supply Chain": "#6B8E8E",
};

// Helper to render clickable links - handles both string and object formats
const renderLink = (item, colorClass = "text-blue-400") => {
  // Handle object format {title, link}
  if (item && typeof item === 'object' && (item.title || item.link)) {
    const title = item.title || item.link || 'Link';
    const link = item.link || '';
    if (link && (link.startsWith('http://') || link.startsWith('https://'))) {
      return (
        <a href={link} target="_blank" rel="noopener noreferrer" className={`${colorClass} hover:underline break-all`}>
          {title.length > 60 ? title.substring(0, 60) + '...' : title}
          <ExternalLink className="h-3 w-3 inline ml-1" />
        </a>
      );
    }
    return <span className="text-gray-700">{title}</span>;
  }
  
  // Handle string format
  if (item && typeof item === 'string') {
    if (item.startsWith('http://') || item.startsWith('https://')) {
      return (
        <a href={item} target="_blank" rel="noopener noreferrer" className={`${colorClass} hover:underline break-all`}>
          {item.length > 50 ? item.substring(0, 50) + '...' : item}
          <ExternalLink className="h-3 w-3 inline ml-1" />
        </a>
      );
    }
    // Check if string contains URL
    if (item.includes('http')) {
      const parts = item.split('http');
      if (parts.length > 1) {
        const title = parts[0].trim().replace(/:$/, '').trim() || 'Link';
        const url = 'http' + parts[1].split(/\s/)[0];
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className={`${colorClass} hover:underline break-all`}>
            {title.length > 60 ? title.substring(0, 60) + '...' : title}
            <ExternalLink className="h-3 w-3 inline ml-1" />
          </a>
        );
      }
    }
    return <span className="text-gray-700">{item}</span>;
  }
  return null;
};

// Helper to get activity icon and label
const getActivityDisplay = (activityType) => {
  const displays = {
    books_to_read: { icon: BookOpen, label: 'Books to Read', color: 'text-amber-400' },
    case_studies: { icon: FileText, label: 'Case Studies', color: 'text-emerald-400' },
    courses_outside_mu: { icon: GraduationCap, label: 'Courses (Outside MU)', color: 'text-blue-400' },
    youtube_channels: { icon: Youtube, label: 'YouTube Channels', color: 'text-red-400' },
    podcasts: { icon: Briefcase, label: 'Podcasts', color: 'text-purple-400' },
    websites_to_follow: { icon: Globe, label: 'Websites', color: 'text-blue-400' },
    blogs_to_follow: { icon: FileText, label: 'Blogs', color: 'text-teal-400' },
    skills_to_learn: { icon: Star, label: 'Skills', color: 'text-yellow-400' },
    guesstimates: { icon: Target, label: 'Guesstimates', color: 'text-orange-400' },
    daily_tasks: { icon: CheckCircle2, label: 'Daily Tasks', color: 'text-green-400' },
    competitions: { icon: TrendingUp, label: 'Competitions', color: 'text-pink-400' },
    clubs_to_join: { icon: Users, label: 'Clubs & Committees', color: 'text-indigo-400' },
    prep_materials: { icon: BookOpen, label: 'Prep Materials', color: 'text-cyan-400' },
    newsletters: { icon: FileText, label: 'Newsletters', color: 'text-orange-400' },
    linkedin_samples: { icon: ExternalLink, label: 'LinkedIn', color: 'text-blue-400' },
    product_teardowns: { icon: Lightbulb, label: 'Product Teardowns', color: 'text-yellow-400' },
    documents: { icon: FileText, label: 'Documents', color: 'text-gray-500' },
    miscellaneous: { icon: AlertCircle, label: 'Other', color: 'text-gray-500' },
    other_resources: { icon: AlertCircle, label: 'Resources', color: 'text-gray-500' },
  };
  return displays[activityType] || { icon: FileText, label: activityType.replace(/_/g, ' '), color: 'text-gray-500' };
};

export default function StudentReport() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [swotOpen, setSwotOpen] = useState({ strengths: true, weaknesses: true, opportunities: true, threats: true });
  const [exporting, setExporting] = useState(false);
  const chartsRef = useRef(null);
  
  // Domain toggle states
  const [mentorDomain, setMentorDomain] = useState("primary");
  const [prepDomain, setPrepDomain] = useState("primary");
  const [academicDomain, setAcademicDomain] = useState("primary");

  useEffect(() => {
    fetchData();
  }, [studentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentRes, reportRes] = await Promise.allSettled([
        axios.get(`${API}/students/${studentId}`),
        axios.get(`${API}/reports/${studentId}`),
      ]);
      if (studentRes.status === "fulfilled") setStudent(studentRes.value.data);
      if (reportRes.status === "fulfilled") setReport(reportRes.value.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/reports/generate/${studentId}`);
      setReport(response.data);
      toast.success("Report generated successfully");
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(false);
    }
  };

  const handlePdfExport = async () => {
    if (!report || !student) return;
    setExporting(true);
    toast.info("Generating PDF report...");

    try {
      const response = await fetch(`${API}/students/${studentId}/pdf`, { headers: authHeaders() });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to generate PDF");
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Career_Analysis_${(student.name || 'Student').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error(error.message || "Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Prepare chart data - skill name mapping for better display
  const skillNameMapping = {
    "Cultural Fit": "Adaptability",
    "Domain Expertise": "Domain\nExpertise",
    "Problem Solving": "Problem\nSolving",
    "Analytical Skills": "Analytical",
    "Technical Skills": "Technical",
    "Critical Thinking": "Critical\nThinking",
  };
  
  const skillsData = report?.skills_assessment ? Object.entries(report.skills_assessment).map(([skill, score]) => ({
    skill: skillNameMapping[skill] || skill,
    score: score,
    fullMark: 10,
  })) : [];

  // Domain name mapping for full display
  const domainNameMapping = {
    "Finance / PE / VC": "Finance /\nPE / VC",
    "Product Management": "Product\nMgmt",
    "Founder's Office / Chief of Staff": "FOCOS",
    "Operations & Supply Chain": "Operations /\nSupply Chain",
  };

  const domainData = report?.domain_scores ? Object.entries(report.domain_scores)
    .map(([domain, score]) => ({
      domain: domainNameMapping[domain] || domain,
      fullDomain: domain,
      score: score,
      fill: DOMAIN_COLORS[domain] || "#888",
    }))
    .sort((a, b) => b.score - a.score) : [];

  // Get courses for selected domain
  const getCoursesForDomain = () => {
    if (!report?.academic_roadmap) {
      // Fallback to old structure
      return report?.recommended_courses || [];
    }
    return academicDomain === "primary" 
      ? (report.academic_roadmap.primary || [])
      : (report.academic_roadmap.secondary || []);
  };

  // Get mentors for selected domain
  const getMentorsForDomain = () => {
    if (!report?.recommended_mentors) return { external: [], internal: [] };
    
    if (typeof report.recommended_mentors === 'object' && !Array.isArray(report.recommended_mentors)) {
      const external = mentorDomain === "primary" 
        ? (report.recommended_mentors.primary || [])
        : (report.recommended_mentors.secondary || []);
      const internal = mentorDomain === "primary"
        ? (report.recommended_mentors.internal_primary || [])
        : (report.recommended_mentors.internal_secondary || []);
      return { external, internal };
    }
    // Old format fallback
    return { external: report.recommended_mentors || [], internal: [] };
  };

  // Get prep roadmap for selected domain
  const getPrepRoadmap = () => {
    if (!report?.prep_roadmap) return null;
    return prepDomain === "primary" 
      ? report.prep_roadmap.primary 
      : report.prep_roadmap.secondary;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#E8B923]" />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <p className="text-gray-500">Student not found</p>
        <Button onClick={() => navigate("/students")} className="mt-4">Back to Students</Button>
      </div>
    );
  }

  const courses = getCoursesForDomain();
  const { external: externalMentors, internal: internalMentors } = getMentorsForDomain();
  const prepRoadmap = getPrepRoadmap();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/students")} className="text-gray-500 hover:text-gray-900">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{student.name}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={generateReport} disabled={generating} variant="outline" className="border-gray-200 text-gray-700">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Regenerate Report
          </Button>
          <Button onClick={handlePdfExport} disabled={exporting || !report} className="bg-[#E8B923] text-black hover:bg-[#D4A017]">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Download PDF
          </Button>
        </div>
      </div>

      {!report ? (
        <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Target className="h-16 w-16 text-[#E8B923] mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Report Generated</h3>
            <p className="text-gray-500 mb-6">Generate a comprehensive career analysis report for this student</p>
            <Button onClick={generateReport} disabled={generating} className="bg-[#E8B923] text-black hover:bg-[#D4A017]">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Generate Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Domain Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-[#E8B923]/20 to-[#E8B923]/5 border-[#E8B923]/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Target className="h-5 w-5 text-[#E8B923]" />
                  <span className="text-sm text-gray-500">Primary Domain</span>
                  <Badge className="ml-auto bg-[#E8B923] text-black">Best Match</Badge>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{report.primary_domain}</h2>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-blue-500/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="h-5 w-5 text-blue-400" />
                  <span className="text-sm text-gray-500">Secondary Domain</span>
                  <Badge className="ml-auto bg-blue-500/20 text-blue-400">Alternative Path</Badge>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{report.secondary_domain}</h2>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-white border border-gray-200 flex-wrap h-auto p-1.5 gap-1" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TabsTrigger value="overview" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
                Overview
              </TabsTrigger>
              <TabsTrigger value="swot" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
                SWOT Analysis
              </TabsTrigger>
              <TabsTrigger value="academic" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
                Academic Roadmap
              </TabsTrigger>
              <TabsTrigger value="prep" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
                Prep Roadmap
              </TabsTrigger>
              <TabsTrigger value="entrepreneurship" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
                Entrepreneurship
              </TabsTrigger>
              <TabsTrigger value="mentors" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
                Mentors
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-4 space-y-4"><SectionErrorBoundary name="overview">
              {/* AI Summary - Full 2 paragraphs */}
              <Card className="bg-white border border-gray-200/80 overflow-hidden relative" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#C49A3C] to-[#E8B923]" />
                <CardHeader>
                  <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-[#E8B923]" />
                    Profile Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-700 text-sm leading-relaxed space-y-4">
                    {(report.ai_insights || "").split('\n\n').map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                    <p className="mt-4">
                      Based on your profile <strong>{report.student_name || student?.name}</strong>, we recommend you to pursue: <strong>{report.primary_domain}</strong> and <strong>{report.secondary_domain}</strong> for your academics and placements journey.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Charts */}
              <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">Skills Assessment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={skillsData}>
                          <PolarGrid stroke="#E5E7EB" />
                          <PolarAngleAxis dataKey="skill" tick={{ fill: '#4B5563', fontSize: 10 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#9CA3AF', fontSize: 8 }} />
                          <Radar name="Skills" dataKey="score" stroke="#E8B923" fill="#E8B923" fillOpacity={0.4} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">Domain Fit Scores</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={domainData} layout="vertical" margin={{ left: 10, right: 10 }}>
                          <XAxis type="number" domain={[0, 100]} tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                          <YAxis type="category" dataKey="domain" tick={{ fill: '#4B5563', fontSize: 9 }} width={100} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }} />
                          <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                            {domainData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </SectionErrorBoundary></TabsContent>

            {/* SWOT Tab */}
            <TabsContent value="swot" className="mt-4"><SectionErrorBoundary name="swot">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "strengths", title: "Strengths", icon: CheckCircle2, colorClass: "text-emerald-400", dotClass: "bg-emerald-400" },
                  { key: "weaknesses", title: "Areas for Development", icon: AlertCircle, colorClass: "text-amber-400", dotClass: "bg-amber-400" },
                  { key: "opportunities", title: "Opportunities", icon: TrendingUp, colorClass: "text-blue-400", dotClass: "bg-blue-400" },
                  { key: "threats", title: "Challenges to Prepare For", icon: AlertCircle, colorClass: "text-red-400", dotClass: "bg-red-400" },
                ].map(({ key, title, icon: Icon, colorClass, dotClass }) => (
                  <Card key={key} className="bg-white border-gray-200">
                    <Collapsible open={swotOpen[key]} onOpenChange={(open) => setSwotOpen((prev) => ({ ...prev, [key]: open }))}>
                      <CollapsibleTrigger className="w-full">
                        <CardHeader className="flex flex-row items-center justify-between">
                          <CardTitle className={`text-lg flex items-center gap-2 ${colorClass}`}>
                            <Icon className="h-5 w-5" />
                            {title}
                          </CardTitle>
                          <ChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${swotOpen[key] ? "rotate-180" : ""}`} />
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent>
                          <ul className="space-y-2">
                            {(report.swot?.[key] || []).map((item, i) => (
                              <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                                <span className={`w-2 h-2 rounded-full ${dotClass} mt-1.5 flex-shrink-0`} />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                ))}
              </div>
            </SectionErrorBoundary></TabsContent>

            {/* Academic Roadmap Tab */}
            <TabsContent value="academic" className="mt-4 space-y-4"><SectionErrorBoundary name="academic">
              {/* Intro text */}
              <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent className="p-4 space-y-3">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    The academic roadmap below is a recommended plan curated for courses that are likely to be most valuable in strengthening your foundations and readiness for domain opportunities. Think of it as a strategic guide rather than a fixed prescription.
                  </p>
                  <p className="text-gray-500 text-sm leading-relaxed italic">
                    Please note: The course structure is designed to support both conceptual knowledge and hands-on practice, with final curriculum details being optimized before commencement.
                  </p>
                </CardContent>
              </Card>

              {/* Domain Toggle */}
              <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <GraduationCap className="h-5 w-5 text-[#E8B923]" />
                    <span className="text-gray-900 font-medium">Domain Selection</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className={academicDomain === "primary" ? "text-[#E8B923]" : "text-gray-500"}>Primary</Label>
                    <Switch
                      checked={academicDomain === "secondary"}
                      onCheckedChange={(checked) => setAcademicDomain(checked ? "secondary" : "primary")}
                    />
                    <Label className={academicDomain === "secondary" ? "text-blue-400" : "text-gray-500"}>Secondary</Label>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-between">
                <p className="text-gray-500 text-sm">
                  Showing courses for: <span className="text-gray-900 font-medium">
                    {academicDomain === "primary" ? report.primary_domain : report.secondary_domain}
                  </span>
                </p>
              </div>

              {/* Timeline view of terms */}
              <div className="relative">
                {/* Timeline connector */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#E8B923] via-[#E8B923]/50 to-transparent hidden md:block" />
                
                {/* Term-wise courses */}
                <div className="space-y-6">
                  {courses.length === 0 && (
                    <EmptyState title="No course plan is available for this domain yet.">
                      Upload the Course Roadmap and Counselling Material on the Data Upload page, then click Refresh Roadmaps on the Students page.
                    </EmptyState>
                  )}
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((term) => {
                    const termCourses = courses.filter((c) => c.term === term);
                    if (termCourses.length === 0) return null;
                    
                    const totalCredits = termCourses.reduce((sum, c) => sum + (c.credits || 0), 0);
                    
                    return (
                      <div key={term} className="relative pl-0 md:pl-16">
                        {/* Timeline marker */}
                        <div className="absolute left-4 top-4 w-5 h-5 rounded-full bg-[#E8B923] border-4 border-white hidden md:flex items-center justify-center z-10">
                          <span className="text-xs font-bold text-black">{term}</span>
                        </div>
                        
                        <Card className="bg-white border-gray-200 overflow-hidden">
                          <CardHeader className="pb-3 bg-gray-50/50">
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg text-gray-900 flex items-center gap-3">
                                <span className="md:hidden flex items-center justify-center w-7 h-7 rounded-full bg-[#E8B923] text-black text-sm font-bold">
                                  {term}
                                </span>
                                Term {term}
                              </CardTitle>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-gray-100 text-gray-600">
                                  {termCourses.filter(c => c.is_core).length} core
                                </Badge>
                                <Badge className="bg-[#E8B923]/20 text-[#B8860B]">
                                  {termCourses.filter(c => !c.is_core).length} elective
                                </Badge>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {termCourses.map((course, i) => (
                                <div
                                  key={i}
                                  className={`p-4 rounded-lg border transition-all hover:scale-[1.01] ${
                                    course.is_core 
                                      ? 'bg-gray-50 border-gray-200 hover:border-gray-300' 
                                      : 'bg-[#E8B923]/5 border-[#E8B923]/20 hover:border-[#E8B923]/40'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className={`w-2 h-2 rounded-full ${course.is_core ? 'bg-gray-400' : 'bg-[#E8B923]'}`} />
                                        <span className="text-gray-900 text-sm font-medium">{course.course_name}</span>
                                      </div>
                                      <p className="text-gray-500 text-xs pl-4">{course.concentration}</p>
                                    </div>
                                    <Badge variant="outline" className={`text-xs shrink-0 ${
                                      course.is_core 
                                        ? 'text-gray-500 border-gray-300 bg-gray-50'
                                        : 'text-[#B8860B] border-[#E8B923]/50 bg-[#E8B923]/10'
                                    }`}>
                                      {course.is_core ? 'Core' : 'Elective'}
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            </SectionErrorBoundary></TabsContent>

            {/* Prep Roadmap Tab */}
            <TabsContent value="prep" className="mt-4 space-y-6"><SectionErrorBoundary name="prep">
              {/* Domain Toggle */}
              <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Map className="h-5 w-5 text-[#E8B923]" />
                    <span className="text-gray-900 font-medium">Prep Domain</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className={prepDomain === "primary" ? "text-[#E8B923]" : "text-gray-500"}>Primary</Label>
                    <Switch
                      checked={prepDomain === "secondary"}
                      onCheckedChange={(checked) => setPrepDomain(checked ? "secondary" : "primary")}
                    />
                    <Label className={prepDomain === "secondary" ? "text-blue-400" : "text-gray-500"}>Secondary</Label>
                  </div>
                </CardContent>
              </Card>

              <p className="text-gray-500 text-sm">
                Career prep roadmap for: <span className="text-gray-900 font-medium">
                  {prepDomain === "primary" ? report.primary_domain : report.secondary_domain}
                </span>
                {prepRoadmap?.target_role && (
                  <Badge className="ml-2 bg-[#E8B923]/20 text-[#E8B923]">{prepRoadmap.target_role}</Badge>
                )}
              </p>
            {!(Object.keys(prepRoadmap?.resources || {}).length || Object.keys(prepRoadmap?.cat_prep_roadmap || {}).length) && (
              <EmptyState title="No prep roadmap is available for this domain yet.">
                {"Upload the Counselling Material workbook on the Data Upload page (it needs a sheet for this domain), then click Refresh Roadmaps."}
              </EmptyState>
            )}

              {/* RESOURCES Section (NOT term-wise) */}
              {prepRoadmap?.resources && Object.keys(prepRoadmap.resources).length > 0 && (
                <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardHeader className="bg-gradient-to-r from-[#E8B923]/20 to-[#E8B923]/10 border-b border-gray-200">
                    <CardTitle className="text-lg text-[#E8B923] font-semibold text-center flex items-center justify-center gap-2">
                      <FileText className="h-5 w-5" />
                      Resources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full border-collapse">
                      <tbody>
                        {prepRoadmap.resources.cv_samples?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50 w-[200px]">CV Sample</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.cv_samples.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.cv_enhancers?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">CV Enhancers</td>
                            <td className="border-b border-gray-200 p-4 text-gray-700">
                              {prepRoadmap.resources.cv_enhancers.map((item, i) => (
                                <div key={i}>{typeof item === 'object' ? item.title : item}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.portfolio?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">Portfolio</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.portfolio.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.linkedin?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">LinkedIn</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.linkedin.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.youtube?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">YouTube Channels</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.youtube.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.blogs?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">Blogs to Follow</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.blogs.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.websites?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">Websites to Follow</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.websites.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.newsletters?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">Newsletters</td>
                            <td className="border-b border-gray-200 p-4">
                              {prepRoadmap.resources.newsletters.map((item, i) => (
                                <div key={i}>{renderLink(item, "text-blue-400")}</div>
                              ))}
                            </td>
                          </tr>
                        )}
                        {prepRoadmap.resources.skills?.length > 0 && (
                          <tr className="hover:bg-gray-50 transition-colors">
                            <td className="border-b border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">Skills</td>
                            <td className="border-b border-gray-200 p-4">
                              <div className="flex flex-wrap gap-2">
                                {prepRoadmap.resources.skills.map((skill, i) => (
                                  <Badge key={i} className="bg-[#E8B923]/20 text-[#E8B923] border border-[#E8B923]/30">{skill}</Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Term-wise Prep Resources Table */}
              {prepRoadmap?.cat_prep_roadmap && Object.keys(prepRoadmap.cat_prep_roadmap).length > 0 && (
                <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardHeader className="bg-gradient-to-r from-[#E8B923]/20 to-[#E8B923]/10 border-b border-gray-200">
                    <CardTitle className="text-lg text-[#E8B923] font-semibold text-center flex items-center justify-center gap-2">
                      <BookOpen className="h-5 w-5" />
                      Term-wise Prep Resources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border-b border-r border-gray-200 p-4 text-left text-gray-500 font-semibold min-w-[180px]"></th>
                            {Object.keys(prepRoadmap.cat_prep_roadmap)
                              .sort((a, b) => {
                                const numA = parseInt(a.replace('term_', ''));
                                const numB = parseInt(b.replace('term_', ''));
                                return numA - numB;
                              })
                              .map((term) => (
                                <th key={term} className="border-b border-r border-gray-200 p-4 text-center min-w-[200px] bg-gray-50">
                                  <span className="text-[#E8B923] font-bold text-lg">T{term.replace('term_', '')}</span>
                                </th>
                              ))}
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { key: 'courses', label: 'Courses (Other than MU)', color: 'text-blue-400' },
                            { key: 'books', label: 'Books to Read', color: 'text-amber-400' },
                            { key: 'case_studies', label: 'Case Studies', color: 'text-emerald-400' },
                            { key: 'guesstimates', label: 'Guesstimates', color: 'text-purple-400' },
                            { key: 'product_teardowns', label: 'Product Teardowns', color: 'text-pink-400' },
                            { key: 'documents', label: 'Documents', color: 'text-cyan-400' },
                            { key: 'daily_tasks', label: 'Daily Tasks', color: 'text-green-400' },
                            { key: 'skills', label: 'Skills', color: 'text-orange-400' },
                            { key: 'miscellaneous', label: 'Miscellaneous', color: 'text-gray-500' },
                          ].map(({ key, label, color }) => {
                            const hasData = Object.values(prepRoadmap.cat_prep_roadmap).some(
                              (termData) => termData[key] && termData[key].length > 0
                            );
                            if (!hasData) return null;
                            
                            return (
                              <tr key={key} className="hover:bg-gray-50 transition-colors">
                                <td className="border-b border-r border-gray-200 p-4 text-gray-500 font-medium bg-gray-50">
                                  {label}
                                </td>
                                {Object.keys(prepRoadmap.cat_prep_roadmap)
                                  .sort((a, b) => {
                                    const numA = parseInt(a.replace('term_', ''));
                                    const numB = parseInt(b.replace('term_', ''));
                                    return numA - numB;
                                  })
                                  .map((term) => {
                                    const items = prepRoadmap.cat_prep_roadmap[term]?.[key] || [];
                                    return (
                                      <td key={term} className="border-b border-r border-gray-200 p-4 align-top">
                                        <div className="space-y-2">
                                          {items.map((item, i) => (
                                            <div key={i} className="text-sm">
                                              {renderLink(item, color)}
                                            </div>
                                          ))}
                                          {items.length === 0 && (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </div>
                                      </td>
                                    );
                                  })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </SectionErrorBoundary></TabsContent>

            {/* Entrepreneurship Tab */}
            <TabsContent value="entrepreneurship" className="mt-4 space-y-4"><SectionErrorBoundary name="entrepreneurship">
              {/* Intro text */}
              <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent className="p-4">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Start with an idea, scale it with vision, and lead with conviction. Here is a target industry which best fits your knowledge, experience and skills, and three startup ideas waiting for implementation.
                  </p>
                </CardContent>
              </Card>

              {/* Target Industry */}
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-purple-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Briefcase className="h-5 w-5 text-purple-400" />
                    <span className="text-sm text-gray-500">Target Industry</span>
                  </div>
                  <p className="text-gray-900">{report.entrepreneurship?.target_industry || "Based on your background, consider tech-enabled solutions in your domain."}</p>
                </CardContent>
              </Card>

              {/* Startup Ideas */}
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Rocket className="h-5 w-5 text-[#E8B923]" />
                Startup Ideas
              </h3>
              <div className="grid grid-cols-1 gap-4">
                {(report.entrepreneurship?.startup_ideas || []).length === 0 && (
              <EmptyState title="Startup ideas haven't been generated for this report yet.">
                {"Regenerate this report to create them."}
              </EmptyState>
            )}
            {(report.entrepreneurship?.startup_ideas || []).map((idea, i) => (
                  <Card key={i} className="bg-white border-gray-200">
                    <CardHeader>
                      <CardTitle className="text-lg text-[#E8B923] flex items-center gap-2">
                        <span className="bg-[#E8B923] text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                          {i + 1}
                        </span>
                        {idea.idea_name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-gray-700">{idea.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="text-sm text-gray-500 mb-2">TAM / SAM / SOM</h4>
                          <p className="text-gray-700 text-sm">{idea.tam_sam_som}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="text-sm text-gray-500 mb-2">Operations Insight</h4>
                          <p className="text-gray-700 text-sm">{idea.operations_insight}</p>
                        </div>
                      </div>

                      {idea.market_analysis && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(idea.market_analysis).map(([key, value]) => (
                            <div key={key} className="p-2 bg-gray-50 rounded border border-gray-200 text-center">
                              <p className="text-xs text-gray-500 capitalize">{key.replace("_", " ")}</p>
                              <p className={`text-sm font-medium ${levelColor(value)}`}>{String(value ?? "")}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                        <h4 className="text-sm text-emerald-400 mb-1">Why This Suits You</h4>
                        <p className="text-gray-700 text-sm">{idea.why_this_student}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Outclass VP Challenge Timeline */}
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mt-6">
                <BarChart3 className="h-5 w-5 text-purple-400" />
                Outclass VP Challenge Roadmap
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {report.entrepreneurship?.outclass_timeline && Object.entries(report.entrepreneurship.outclass_timeline).map(([termKey, term]) => (
                  <Card key={termKey} className="bg-white border-gray-200 hover:border-purple-500/30 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base text-gray-900 flex items-center justify-between">
                        <span>{termKey.replace("_", " ").toUpperCase()}</span>
                        <Badge className="bg-purple-500/20 text-purple-400">{term.credits} cr</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <h4 className="text-[#E8B923] font-semibold mb-1">{term.name}</h4>
                      <p className="text-gray-500 text-sm mb-2">{term.title}</p>
                      <p className="text-gray-500 text-xs">{term.description}</p>
                      <p className="text-xs text-gray-400 mt-2">{term.dates}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </SectionErrorBoundary></TabsContent>

            {/* Mentors Tab */}
            <TabsContent value="mentors" className="mt-4 space-y-4"><SectionErrorBoundary name="mentors">
              {/* Intro text */}
              <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent className="p-4">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    We have an on demand in-house mentorship platform - Mentor Union which hosts more than 500 mentors. Your access enables from Day 1. Following are the top mentors we recommend to you for your journey at Masters' Union.
                  </p>
                </CardContent>
              </Card>

              {/* Domain Toggle */}
              <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Users className="h-5 w-5 text-[#E8B923]" />
                    <span className="text-gray-900 font-medium">Mentor Domain</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className={mentorDomain === "primary" ? "text-[#E8B923]" : "text-gray-500"}>Primary</Label>
                    <Switch
                      checked={mentorDomain === "secondary"}
                      onCheckedChange={(checked) => setMentorDomain(checked ? "secondary" : "primary")}
                    />
                    <Label className={mentorDomain === "secondary" ? "text-blue-400" : "text-gray-500"}>Secondary</Label>
                  </div>
                </CardContent>
              </Card>

              <p className="text-gray-500 text-sm">
                Showing mentors for: <span className="text-gray-900 font-medium">
                  {mentorDomain === "primary" ? report.primary_domain : report.secondary_domain}
                </span>
              </p>

              {/* Mentors */}
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mt-4">
                <Users className="h-5 w-5 text-[#E8B923]" />
                Mentors
              </h3>
              {externalMentors.length === 0 ? (
                <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  <CardContent className="p-6 text-center text-gray-500">
                    No mentors found for this domain
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {externalMentors.map((mentor, i) => (
                    <Card key={i} className="bg-white border-gray-200 hover:border-[#E8B923]/30 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#E8B923]/20 flex items-center justify-center">
                              <Users className="h-6 w-6 text-[#E8B923]" />
                            </div>
                            <div>
                              <h4 className="text-gray-900 font-medium">{mentor.name}</h4>
                              <p className="text-gray-500 text-sm">{mentor.designation}</p>
                              <p className="text-gray-400 text-xs">{mentor.company}</p>
                            </div>
                          </div>
                          {mentor.rating > 0 && (
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-[#E8B923] fill-[#E8B923]" />
                              <span className="text-gray-500 text-sm">{mentor.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        {mentor.expertise?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {mentor.expertise.slice(0, 4).map((e, j) => (
                              <Badge key={j} className="text-xs bg-gray-100 text-gray-600">{e}</Badge>
                            ))}
                          </div>
                        )}
                        {mentor.linkedin && (
                          <a
                            href={mentor.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center gap-1 text-blue-400 hover:underline text-sm"
                          >
                            <ExternalLink className="h-3 w-3" />
                            LinkedIn Profile
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </SectionErrorBoundary></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
